import { useEffect, useState } from "react";
import { FloppyDisk, CreditCard, WhatsappLogo, Buildings, CheckCircle, Copy, PaperPlaneTilt, ArrowClockwise, Quotes as Quotes_, Plus, Trash, Cake, ChartBar, ImageSquare, UploadSimple, IdentificationCard } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Switch } from "../components/ui/switch";
import { API, api, errMsg, useApi, fmtDate } from "../lib/api";
import { useT } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { PageHeader, GlassCard, Field, Loader, Empty, Table, Badge, DataTable } from "../components/kit";

const PROFILE = [["name", "School name"], ["principal_name", "Principal"], ["phone", "Phone"], ["email", "Email"], ["address", "Address"], ["city", "City"], ["academic_year", "Academic year"], ["logo_url", "Logo URL"]];

function Profile({ school, reload, t }) {
  const [f, setF] = useState({});
  useEffect(() => {
    setF(Object.fromEntries(PROFILE.map(([k]) => [k, school[k] || ""])));
  }, [school]);
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put("/school", f);
      toast.success("Profile saved");
      reload();
    } catch (er) {
      toast.error(errMsg(er));
    }
  };
  return (
    <GlassCard className="p-6 sm:p-8">
      <div className="mb-6 flex items-center gap-3"><Buildings size={22} weight="duotone" className="text-blue-300" /><h2 className="font-display text-base md:text-lg text-white">{t("School profile")}</h2><span className="ml-auto text-xs text-zinc-500">{`Code ${school.code}`}</span></div>
      <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {PROFILE.map(([k, l]) => <Field key={k} label={l}><input className="field" value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`settings-${k}-input`} /></Field>)}
        <button className="btn-primary sm:col-span-2 sm:justify-self-start" data-testid="settings-profile-save"><FloppyDisk size={16} /> {t("Save")}</button>
      </form>
    </GlassCard>
  );
}

function Payments({ school, reload, t }) {
  const [f, setF] = useState({ key_id: school.razorpay_key_id || "", key_secret: "", webhook_secret: "" });
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put("/school/payment-keys", f);
      toast.success("Razorpay settings saved");
      setF({ ...f, key_secret: "", webhook_secret: "" });
      reload();
    } catch (er) {
      toast.error(errMsg(er));
    }
  };
  return (
    <GlassCard className="p-6 sm:p-8">
      <div className="mb-2 flex items-center gap-3"><CreditCard size={22} weight="duotone" className="text-orange-300" /><h2 className="font-display text-base md:text-lg text-white">{t("Online payments")} · Razorpay</h2>
        <span className="ml-auto">{school.payments_connected ? <Badge status="paid">Connected</Badge> : <Badge status="pending">Not connected</Badge>}</span></div>
      <p className="mb-6 text-sm text-zinc-400">Parents pay directly into your school's own Razorpay account. Find keys in Razorpay Dashboard → Account & Settings → API Keys.</p>
      <form onSubmit={save} className="space-y-4">
        <Field label="Key ID"><input className="field font-mono" placeholder="rzp_live_…" value={f.key_id} onChange={(e) => setF({ ...f, key_id: e.target.value })} data-testid="rzp-key-id-input" /></Field>
        <Field label={`Key secret ${school.has_key_secret ? "(saved — leave blank to keep)" : ""}`}><input type="password" className="field font-mono" value={f.key_secret} onChange={(e) => setF({ ...f, key_secret: e.target.value })} data-testid="rzp-key-secret-input" /></Field>
        <Field label={`Webhook secret ${school.has_webhook_secret ? "(saved — leave blank to keep)" : ""}`}><input type="password" className="field font-mono" value={f.webhook_secret} onChange={(e) => setF({ ...f, webhook_secret: e.target.value })} data-testid="rzp-webhook-secret-input" /></Field>
        <div className="rounded-2xl bg-white/5 border border-white/10 p-4">
          <div className="eyebrow !text-[10px] mb-2">Webhook URL — add in Razorpay → Webhooks (events: payment.captured, order.paid, payment.failed)</div>
          <div className="flex items-center gap-2">
            <code className="flex-1 truncate text-xs text-blue-200" data-testid="rzp-webhook-url">{school.webhook_url}</code>
            <button type="button" className="btn-ghost !p-2" onClick={() => { navigator.clipboard?.writeText(school.webhook_url); toast.success("Copied"); }} data-testid="copy-webhook-url"><Copy size={14} /></button>
          </div>
        </div>
        <button className="btn-primary" data-testid="rzp-save-btn"><FloppyDisk size={16} /> {t("Save")}</button>
      </form>
    </GlassCard>
  );
}

const WA_HELP = "Alerts are sent from your school's own WhatsApp Business number. In Meta for Developers → WhatsApp → API Setup, copy the Phone Number ID and create a permanent system-user token. Create a Utility template with two body variables, for example \"{{1}}: {{2}}\", and wait for Meta to approve it.";

function WhatsAppConnect({ school, reload, t }) {
  const [f, setF] = useState({ phone_number_id: school.whatsapp_phone_number_id || "", access_token: "", app_secret: "",
    template_name: school.whatsapp_template || "school_update", language_code: school.whatsapp_language || "en" });
  const [testPhone, setTestPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put("/school/whatsapp", f);
      toast.success("WhatsApp settings saved");
      setF({ ...f, access_token: "", app_secret: "" });
      reload();
    } catch (er) {
      toast.error(errMsg(er));
    }
  };
  const test = async () => {
    setBusy(true);
    try {
      await api.post("/school/whatsapp/test", { phone: testPhone });
      toast.success("Test message sent");
    } catch (er) {
      toast.error(errMsg(er));
    } finally {
      setBusy(false);
    }
  };
  const copy = (v) => { navigator.clipboard?.writeText(v); toast.success("Copied"); };
  return (
    <GlassCard className="p-6 sm:p-8 lg:col-span-2">
      <div className="mb-2 flex items-center gap-3"><WhatsappLogo size={22} weight="duotone" className="text-emerald-300" /><h2 className="font-display text-base md:text-lg text-white">WhatsApp · Meta Cloud API</h2>
        <span className="ml-auto">{school.whatsapp_connected ? <Badge status="paid" testid="wa-status">Connected</Badge> : <Badge status="pending" testid="wa-status">Not connected</Badge>}</span></div>
      <p className="mb-6 text-sm text-zinc-400">{WA_HELP}</p>
      <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Phone Number ID"><input className="field font-mono" placeholder="1234567890123456" value={f.phone_number_id} onChange={set("phone_number_id")} data-testid="wa-phone-id-input" /></Field>
        <Field label={`Access token ${school.has_wa_token ? "(saved — leave blank to keep)" : ""}`}><input type="password" className="field font-mono" value={f.access_token} onChange={set("access_token")} data-testid="wa-token-input" /></Field>
        <Field label="Approved template name"><input className="field font-mono" value={f.template_name} onChange={set("template_name")} data-testid="wa-template-input" /></Field>
        <Field label="Template language">
          <select className="field" value={f.language_code} onChange={set("language_code")} data-testid="wa-language-select">
            <option value="en">English (en)</option><option value="en_US">English US (en_US)</option><option value="hi">Hindi (hi)</option>
          </select>
        </Field>
        <Field label={`App secret — for delivery receipts ${school.has_wa_app_secret ? "(saved — leave blank to keep)" : "(optional)"}`} className="md:col-span-2"><input type="password" className="field font-mono" value={f.app_secret} onChange={set("app_secret")} data-testid="wa-app-secret-input" /></Field>
        <button className="btn-primary md:justify-self-start" data-testid="wa-save-btn"><FloppyDisk size={16} /> {t("Save")}</button>
      </form>
      {school.whatsapp_verify_token && (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
          {[["Webhook callback URL (Meta → WhatsApp → Configuration, field: messages)", school.whatsapp_webhook_url, "wa-webhook-url"], ["Verify token", school.whatsapp_verify_token, "wa-verify-token"]].map(([l, v, id]) => (
            <div key={id} className="rounded-2xl bg-white/5 border border-white/10 p-4">
              <div className="eyebrow !text-[10px] mb-2">{l}</div>
              <div className="flex items-center gap-2"><code className="flex-1 truncate text-xs text-emerald-200" data-testid={id}>{v}</code>
                <button type="button" className="btn-ghost !p-2" onClick={() => copy(v)} data-testid={`copy-${id}`}><Copy size={14} /></button></div>
            </div>
          ))}
        </div>
      )}
      <div className="mt-6 flex flex-col sm:flex-row gap-3">
        <input className="field sm:!w-64" placeholder="+91 98xxxxxxxx" value={testPhone} onChange={(e) => setTestPhone(e.target.value)} data-testid="wa-test-phone-input" />
        <button className="btn-ghost" disabled={busy || !school.whatsapp_connected || !testPhone} onClick={test} data-testid="wa-test-btn"><PaperPlaneTilt size={16} /> {busy ? "Sending…" : "Send test message"}</button>
      </div>
    </GlassCard>
  );
}

function WhatsApp({ school, reload, t }) {
  const { data, loading, reload: reloadOutbox } = useApi("/school/notifications");
  const toggle = async (v) => {
    try {
      await api.put("/school", { whatsapp_enabled: v });
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const sendQueued = async () => {
    try {
      const r = await api.post("/school/whatsapp/send-queued");
      toast.success(`Sending ${r.data.processed} message(s)`);
      setTimeout(reloadOutbox, 2500);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const pending = (data || []).filter((n) => ["queued", "failed", "sending"].includes(n.status) && Date.now() - new Date(n.created_at) < 3 * 864e5).length;
  return (
    <GlassCard className="p-6 sm:p-8 lg:col-span-2">
      <div className="mb-2 flex flex-wrap items-center gap-3"><WhatsappLogo size={22} weight="duotone" className="text-emerald-300" /><h2 className="font-display text-base md:text-lg text-white">{t("WhatsApp notifications")}</h2>
        <div className="ml-auto flex items-center gap-3">
          <button className="btn-ghost !py-1.5" onClick={reloadOutbox} data-testid="wa-refresh-btn"><ArrowClockwise size={15} /></button>
          {school.whatsapp_connected && pending > 0 && <button className="btn-ghost !py-1.5" onClick={sendQueued} data-testid="wa-send-queued-btn"><PaperPlaneTilt size={15} /> {`Send ${pending} pending`}</button>}
          <Switch checked={!!school.whatsapp_enabled} onCheckedChange={toggle} data-testid="whatsapp-toggle" />
        </div></div>
      <p className="mb-6 text-sm text-zinc-400">{school.whatsapp_connected ? "Absence, fee, homework, notice and result alerts go out automatically and their delivery status appears below." : "Not connected yet — alerts wait here as queued and are also shown in each parent's Updates feed."}</p>
      {loading ? <Loader /> : !data?.length ? <Empty /> : (
        <Table testid="whatsapp-outbox-table">
          <thead><tr><th>{t("Date")}</th><th>Type</th><th>To</th><th>{t("Phone")}</th><th>Message</th><th>{t("Status")}</th></tr></thead>
          <tbody>
            {data.slice(0, 40).map((n) => (
              <tr key={n.id}><td>{fmtDate(n.created_at)}</td><td className="capitalize">{n.kind}</td><td>{n.to_name}</td><td>{n.to_phone || "—"}</td>
                <td className="max-w-[340px] truncate" title={n.message}>{n.title}: {n.message}</td>
                <td title={n.error || ""}><Badge status={n.status}>{n.status}</Badge></td></tr>
            ))}
          </tbody>
        </Table>
      )}
    </GlassCard>
  );
}

function Quotes({ school, reload }) {
  const [list, setList] = useState(school.quotes || []);
  const [text, setText] = useState("");
  const [by, setBy] = useState("");
  const save = async (next) => {
    try {
      await api.put("/school/quotes", { quotes: next });
      setList(next);
      toast.success("Quotes saved");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const add = (e) => {
    e.preventDefault();
    if (text.trim().length < 3) return;
    save([...list, { text: text.trim(), by: by.trim() }]);
    setText("");
    setBy("");
  };
  return (
    <GlassCard className="p-6 sm:p-8 lg:col-span-2" data-testid="settings-quotes">
      <div className="flex items-center gap-3"><Quotes_ size={22} className="text-orange-300" /><h3 className="font-display text-lg font-semibold">Welcome quotes</h3></div>
      <p className="mt-1 text-sm text-zinc-400">Your school's quotes are mixed with the 10 built-in ones. A different quote appears in the welcome screen each day.</p>
      <form onSubmit={add} className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-[1fr_220px_auto]">
        <input className="field" placeholder="Quote (English or Hindi)" maxLength={300} value={text} onChange={(e) => setText(e.target.value)} data-testid="quote-text-input" />
        <input className="field" placeholder="Said by (optional)" maxLength={80} value={by} onChange={(e) => setBy(e.target.value)} data-testid="quote-by-input" />
        <button className="btn-primary" disabled={text.trim().length < 3 || list.length >= 50} data-testid="quote-add-btn"><Plus size={16} /> Add</button>
      </form>
      {!list.length ? <p className="mt-5 text-sm text-zinc-500" data-testid="quotes-empty">No school quotes yet. The built-in ones are being shown.</p> : (
        <ul className="mt-5 space-y-2" data-testid="quotes-list">
          {list.map((q, i) => (
            <li key={i} className="flex items-start gap-3 rounded-2xl border border-white/[0.07] bg-white/[0.03] px-4 py-3" data-testid={`quote-item-${i}`}>
              <div className="flex-1 text-sm"><span className="italic text-zinc-200">“{q.text}”</span>{q.by && <span className="ml-2 text-xs text-zinc-500">— {q.by}</span>}</div>
              <button className="text-zinc-500 transition-colors hover:text-rose-300" onClick={() => save(list.filter((_, j) => j !== i))} data-testid={`quote-delete-${i}`} aria-label="Delete quote"><Trash size={16} /></button>
            </li>
          ))}
        </ul>
      )}
    </GlassCard>
  );
}

function BirthdayWishes({ school, reload, t }) {
  const [on, setOn] = useState(school.birthday_wishes !== false);
  const [msg, setMsg] = useState(school.birthday_message || school.default_birthday_message || "");
  const put = async (body, ok) => {
    try {
      await api.put("/school", body);
      toast.success(ok);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const runNow = async () => {
    try {
      const r = await api.post("/school/birthday-wishes/run");
      toast.success(r.data.sent ? `${r.data.sent} birthday wish(es) queued` : "No new birthday wishes to send today");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const preview = msg.replaceAll("{name}", "Aarav").replaceAll("{full_name}", "Aarav Sharma").replaceAll("{school}", school.name);
  return (
    <GlassCard className="p-6 sm:p-8 lg:col-span-2" data-testid="settings-birthday">
      <div className="flex flex-wrap items-center gap-3">
        <Cake size={22} weight="duotone" className="text-pink-300" />
        <h3 className="font-display text-lg font-semibold">{t("Birthday wishes on WhatsApp")}</h3>
        <div className="ml-auto flex items-center gap-2 text-sm text-zinc-300">
          <span>{on ? "On" : "Off"}</span>
          <Switch checked={on} onCheckedChange={(v) => { setOn(v); put({ birthday_wishes: v }, v ? "Birthday wishes turned on" : "Birthday wishes turned off"); }} data-testid="birthday-toggle" />
        </div>
      </div>
      <p className="mt-1 text-sm text-zinc-400">Every day at 7:00 AM (India time) parents of students with a birthday get this message on WhatsApp. It is also saved in the app's Updates.</p>
      <Field label="Message" className="mt-5">
        <textarea className="field min-h-[96px]" maxLength={500} value={msg} onChange={(e) => setMsg(e.target.value)} data-testid="birthday-message-input" />
      </Field>
      <p className="mt-2 text-xs text-zinc-500">{"Use {name} for the first name, {full_name} for the full name and {school} for the school name."}</p>
      <div className="mt-4 rounded-2xl border border-emerald-500/20 bg-emerald-500/5 px-4 py-3 text-sm text-emerald-100" data-testid="birthday-preview"><span className="mr-2 text-xs uppercase tracking-wider text-emerald-300">Preview</span><span>{preview}</span></div>
      <div className="mt-5 flex flex-wrap gap-2">
        <button className="btn-primary" disabled={msg.trim().length < 5} onClick={() => put({ birthday_message: msg.trim() }, "Message saved")} data-testid="birthday-save-btn"><FloppyDisk size={16} /> Save message</button>
        <button className="btn-ghost" onClick={() => { setMsg(school.default_birthday_message); put({ birthday_message: "" }, "Default message restored"); }} data-testid="birthday-reset-btn">Use default</button>
        <button className="btn-ghost" disabled={!on} onClick={runNow} data-testid="birthday-run-btn"><PaperPlaneTilt size={16} /> Send today's wishes now</button>
      </div>
      {!school.whatsapp_connected && <p className="mt-3 text-xs text-amber-300">WhatsApp is not connected yet, so wishes will wait in the outbox until you connect it above.</p>}
    </GlassCard>
  );
}

function StaffBirthdayWishes({ school, reload, t }) {
  const [on, setOn] = useState(school.staff_birthday_wishes !== false);
  const [msg, setMsg] = useState(school.staff_birthday_message || school.default_staff_birthday_message || "");
  const put = async (body, ok) => {
    try {
      await api.put("/school", body);
      toast.success(ok);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const preview = msg.replaceAll("{name}", "Rahul").replaceAll("{full_name}", "Rahul Verma").replaceAll("{school}", school.name);
  return (
    <GlassCard className="p-6 sm:p-8 lg:col-span-2" data-testid="settings-staff-birthday">
      <div className="flex flex-wrap items-center gap-3">
        <Cake size={22} weight="duotone" className="text-orange-300" />
        <h3 className="font-display text-lg font-semibold">{t("Staff birthday wishes")}</h3>
        <div className="ml-auto flex items-center gap-2 text-sm text-zinc-300">
          <span>{on ? "On" : "Off"}</span>
          <Switch checked={on} onCheckedChange={(v) => { setOn(v); put({ staff_birthday_wishes: v }, v ? "Staff wishes turned on" : "Staff wishes turned off"); }} data-testid="staff-birthday-toggle" />
        </div>
      </div>
      <p className="mt-1 text-sm text-zinc-400">Teachers and staff get this message on WhatsApp at 7:00 AM (India time) on their birthday.</p>
      {school.birthday_wishes === false && <p className="mt-2 text-xs text-amber-300" data-testid="staff-birthday-needs-main">Student birthday wishes must be on for staff wishes to be sent.</p>}
      <Field label="Message" className="mt-5">
        <textarea className="field min-h-[96px]" maxLength={500} value={msg} onChange={(e) => setMsg(e.target.value)} data-testid="staff-birthday-message-input" />
      </Field>
      <p className="mt-2 text-xs text-zinc-500">{"Use {name} for the first name, {full_name} for the full name and {school} for the school name."}</p>
      <div className="mt-4 rounded-2xl border border-orange-500/20 bg-orange-500/5 px-4 py-3 text-sm text-orange-100" data-testid="staff-birthday-preview"><span className="mr-2 text-xs uppercase tracking-wider text-orange-300">Preview</span><span>{preview}</span></div>
      <div className="mt-5 flex flex-wrap gap-2">
        <button className="btn-primary" disabled={msg.trim().length < 5} onClick={() => put({ staff_birthday_message: msg.trim() }, "Message saved")} data-testid="staff-birthday-save-btn"><FloppyDisk size={16} /> Save message</button>
        <button className="btn-ghost" onClick={() => { setMsg(school.default_staff_birthday_message); put({ staff_birthday_message: "" }, "Default message restored"); }} data-testid="staff-birthday-reset-btn">Use default</button>
      </div>
    </GlassCard>
  );
}

function WishesReport() {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const { data: d, loading } = useApi(`/school/birthday-wishes/report?month=${month}`);
  const tile = (label, v, tone, id) => (
    <div className="rounded-2xl border border-white/[0.07] bg-white/[0.03] p-4"><div className="text-[11px] text-zinc-400">{label}</div>
      <div className={`mt-1 font-display text-2xl font-semibold tabular-nums ${tone}`} data-testid={id}>{v}</div></div>
  );
  return (
    <GlassCard className="p-6 sm:p-8 lg:col-span-2" data-testid="settings-wishes-report">
      <div className="flex flex-wrap items-center gap-3">
        <ChartBar size={22} weight="duotone" className="text-blue-300" />
        <h3 className="font-display text-lg font-semibold">Birthday wishes report</h3>
        <input type="month" className="field ml-auto !w-auto" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="wishes-report-month" />
      </div>
      {loading || !d ? <Loader /> : (
        <>
          <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-4">
            {tile("Total wishes", d.total, "text-white", "wishes-total")}
            {tile("Students", d.students, "text-blue-300", "wishes-students")}
            {tile("Staff", d.staff, "text-orange-300", "wishes-staff")}
            {tile("Delivered on WhatsApp", d.delivered, "text-emerald-300", "wishes-delivered")}
          </div>
          {!d.rows.length ? <p className="mt-5 text-sm text-zinc-500" data-testid="wishes-report-empty">No birthday wishes were sent this month.</p> : (
            <div className="mt-5 max-h-[340px] overflow-y-auto">
              <DataTable testid="wishes-report-table" minWidth={600} head={["Date", "Name", "For", "Status", "Card"]}
                rows={d.rows.map((r, i) => [fmtDate(r.date), r.name, r.kind === "staff" ? "Staff" : "Student", <Badge status={r.status} />,
                  r.card_url ? <a href={r.card_url} target="_blank" rel="noreferrer" className="text-xs font-medium text-blue-300 hover:text-blue-200" data-testid={`wishes-card-link-${i}`}>View card</a> : "—"])} />
            </div>
          )}
        </>
      )}
    </GlassCard>
  );
}

function SchoolLogo({ school, reload: reloadSchool }) {
  const { refreshMe } = useAuth();
  const reload = () => { reloadSchool(); refreshMe(); };
  const [src, setSrc] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!school.has_logo) {
      setSrc(null);
      return undefined;
    }
    let url;
    api.get("/school/logo", { responseType: "blob" }).then((r) => { url = URL.createObjectURL(r.data); setSrc(url); }).catch(() => setSrc(null));
    return () => url && URL.revokeObjectURL(url);
  }, [school.has_logo, school.logo_version]);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.put("/school/logo", fd);
      toast.success("Logo saved");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    try {
      await api.delete("/school/logo");
      toast.success("Logo removed");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <GlassCard className="p-6 sm:p-8 lg:col-span-2" data-testid="settings-logo">
      <div className="flex items-center gap-3"><ImageSquare size={22} weight="duotone" className="text-blue-300" /><h3 className="font-display text-lg font-semibold">School logo</h3></div>
      <p className="mt-1 text-sm text-zinc-400">Your logo appears in the sidebar, welcome screen, fee receipts, report cards and birthday cards. PNG, JPG or WEBP, up to 5 MB.</p>
      <div className="mt-5 flex flex-wrap items-center gap-5">
        <div className="grid h-24 w-24 place-items-center overflow-hidden rounded-full border border-white/10 bg-white" data-testid="logo-preview">
          {src ? <img src={src} alt="School logo" className="h-20 w-20 object-contain" /> : <ImageSquare size={30} className="text-zinc-400" />}
        </div>
        <label className={`btn-primary cursor-pointer ${busy ? "opacity-60" : ""}`} data-testid="logo-upload-btn">
          <UploadSimple size={16} /> {school.has_logo ? "Change logo" : "Upload logo"}
          <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" disabled={busy} onChange={(e) => upload(e.target.files[0])} data-testid="logo-file-input" />
        </label>
        {school.has_logo && <button className="btn-ghost" onClick={remove} data-testid="logo-remove-btn"><Trash size={16} /> Remove</button>}
      </div>
    </GlassCard>
  );
}

function IdCardBack({ school, reload }) {
  const [rules, setRules] = useState((school.id_card_rules || []).join("\n"));
  const [busy, setBusy] = useState(false);
  const run = async (fn, msg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(msg);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const lines = rules.split("\n").map((r) => r.trim()).filter(Boolean);
  const tooLong = lines.some((r) => r.length > 80);
  const sig = (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    run(() => api.put("/school/signature", fd), "Signature saved");
  };
  return (
    <GlassCard className="p-6 sm:p-8 lg:col-span-2" data-testid="settings-idcard">
      <div className="flex items-center gap-3"><IdentificationCard size={22} weight="duotone" className="text-blue-300" /><h3 className="font-display text-lg font-semibold">ID card back side</h3></div>
      <p className="mt-1 text-sm text-zinc-400">School rules and the principal's signature printed on the back of every student ID card. Emergency contact uses the parent's and school's phone.</p>
      <div className="mt-5 grid gap-6 md:grid-cols-[1fr_260px]">
        <Field label={`School rules — one per line, up to 6 rules of 80 characters (${lines.length}/6)`}>
          <textarea className="field min-h-[150px]" value={rules} onChange={(e) => setRules(e.target.value)} data-testid="idcard-rules-input" />
          {(tooLong || lines.length > 6) && <p className="mt-1 text-xs text-rose-300" data-testid="idcard-rules-error">{tooLong ? "Keep each rule to 80 characters so it fits on the card." : "Use at most 6 rules."}</p>}
        </Field>
        <div>
          <div className="mb-2 text-xs font-medium text-zinc-400">Principal's signature</div>
          <div className="grid h-24 place-items-center rounded-2xl border border-white/10 bg-white" data-testid="signature-preview">
            {school.has_signature ? <img src={`${API}/school/signature?v=${encodeURIComponent(school.signature_version || "")}`} alt="Signature" className="max-h-20 max-w-[220px] object-contain" />
              : <span className="text-xs text-zinc-400">No signature yet</span>}
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <label className={`btn-ghost cursor-pointer !py-1.5 !px-3 ${busy ? "opacity-60" : ""}`} data-testid="signature-upload-btn">
              <UploadSimple size={15} /> {school.has_signature ? "Change" : "Upload"}
              <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" disabled={busy} onChange={(e) => { sig(e.target.files[0]); e.target.value = ""; }} data-testid="signature-file-input" />
            </label>
            {school.has_signature && <button className="btn-ghost !py-1.5 !px-3" disabled={busy} onClick={() => run(() => api.delete("/school/signature"), "Signature removed")} data-testid="signature-remove-btn"><Trash size={15} /> Remove</button>}
          </div>
          <p className="mt-2 text-[11px] text-zinc-500">Tip: a PNG with a transparent background looks best.</p>
        </div>
      </div>
      <button className="btn-primary mt-5" disabled={busy || !lines.length || lines.length > 6 || tooLong} onClick={() => run(() => api.put("/school", { id_card_rules: lines }), "Rules saved")} data-testid="idcard-rules-save-btn">
        <FloppyDisk size={16} /> Save rules
      </button>
    </GlassCard>
  );
}

export default function Settings() {
  const { t } = useT();
  const { data: school, loading, reload } = useApi("/school");
  return (
    <div>
      <PageHeader eyebrow="Configuration" title={t("Settings")} subtitle="School profile, payment gateway and notification channel." />
      {loading || !school ? <Loader /> : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Profile school={school} reload={reload} t={t} />
          <Payments school={school} reload={reload} t={t} />
          <WhatsAppConnect school={school} reload={reload} t={t} />
          <WhatsApp school={school} reload={reload} t={t} />
          <Quotes school={school} reload={reload} />
          <BirthdayWishes school={school} reload={reload} t={t} />
          <SchoolLogo school={school} reload={reload} />
          <IdCardBack school={school} reload={reload} />
          <StaffBirthdayWishes school={school} reload={reload} t={t} />
          <WishesReport />
        </div>
      )}
      <p className="mt-6 flex items-center gap-2 text-xs text-zinc-500"><CheckCircle size={14} /> Secrets are encrypted at rest and never shown again after saving.</p>
    </div>
  );
}
